import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Loader2, Download, XCircle, ArrowLeft } from "lucide-react";
import api, { API } from "../lib/api";
import { UpsellCard } from "../components/BookShop";

const useQuery = () => new URLSearchParams(useLocation().search);

export const PaymentSuccess = () => {
  const sessionId = useQuery().get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [books, setBooks] = useState([]);
  const [upsell, setUpsell] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBooks(data.books || []);
          setUpsell(data.upsell || null);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {}
      if (attempts.current >= 12) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-lg px-4 py-20 text-center md:px-8">
      {state === "checking" && (
        <>
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#e0a82e]" />
          <h1 className="mt-6 font-display text-3xl font-700 uppercase tracking-wide text-[#e0a82e]">Confirming your payment</h1>
          <p className="mt-3 text-[#c0c0c0]">Please wait a moment — we're verifying your payment with Stripe.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="mx-auto h-14 w-14 text-[#4caf7d]" />
          <h1 className="mt-6 font-display text-3xl font-700 uppercase tracking-wide text-[#e0a82e]">Payment confirmed</h1>
          <p className="mt-3 text-[#c0c0c0]">
            Thank you for your purchase of <strong>{books.map((b) => b.title).join(" and ") || "your e-book"}</strong>.{" "}
            {books.length > 1 ? "Your downloads are ready." : "Your download is ready."}
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            {books.map((b) => (
              <a
                key={b.id}
                href={`${API}/ebook/download/${sessionId}?book=${b.id}`}
                data-testid={`download-${b.id}`}
                className="inline-flex w-full max-w-sm items-center justify-center gap-2 rounded bg-[#e0a82e] px-8 py-3 font-display text-base font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f]"
              >
                <Download className="h-5 w-5" /> {books.length > 1 ? `Download ${b.title}` : "Download the eBook (PDF)"}
              </a>
            ))}
          </div>
          <p className="mt-4 text-xs text-[#8a8a8a]">Keep this page — you can re-download from this link. If the download doesn't start, please contact us.</p>
          {upsell && <UpsellCard upsell={upsell} sessionId={sessionId} />}
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="mx-auto h-14 w-14 text-[#e0574c]" />
          <h1 className="mt-6 font-display text-3xl font-700 uppercase tracking-wide text-[#e0574c]">
            {state === "timeout" ? "Still processing" : "Payment not completed"}
          </h1>
          <p className="mt-3 text-[#c0c0c0]">
            {state === "timeout"
              ? "Your payment is taking longer than expected to confirm. If you were charged, refresh this page in a minute — your download will appear once confirmed."
              : "We couldn't confirm a completed payment. If you were charged, please contact us and we'll sort it out."}
          </p>
          <Link to="/e-book" className="mt-8 inline-flex items-center gap-2 font-display text-sm uppercase text-[#e0a82e] hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to the eBook
          </Link>
        </>
      )}
    </div>
  );
};

export const DonationThankYou = () => (
  <div className="mx-auto max-w-lg px-4 py-20 text-center md:px-8" data-testid="donation-thankyou">
    <CheckCircle2 className="mx-auto h-14 w-14 text-[#4caf7d]" />
    <h1 className="mt-6 font-display text-3xl font-700 uppercase tracking-wide text-[#e0a82e]">Thank you</h1>
    <p className="mt-3 text-[#c0c0c0]">
      Your donation means the world and helps this site grow and reach more people affected by incarceration across Australia. We truly appreciate your support.
    </p>
    <Link to="/" className="mt-8 inline-flex items-center gap-2 font-display text-sm uppercase text-[#e0a82e] hover:underline">
      <ArrowLeft className="h-4 w-4" /> Back to Home
    </Link>
  </div>
);

export const PaymentCancel = () => (
  <div className="mx-auto max-w-lg px-4 py-20 text-center md:px-8">
    <XCircle className="mx-auto h-14 w-14 text-[#e0574c]" />
    <h1 className="mt-6 font-display text-3xl font-700 uppercase tracking-wide text-[#e0574c]">Checkout cancelled</h1>
    <p className="mt-3 text-[#c0c0c0]">No payment was taken. You can return and try again whenever you're ready.</p>
    <Link to="/e-book" className="mt-8 inline-flex items-center gap-2 font-display text-sm uppercase text-[#e0a82e] hover:underline">
      <ArrowLeft className="h-4 w-4" /> Back to the eBook
    </Link>
  </div>
);
